import React from "react";
import GenericDashboard from "../GenericDashboard/GenericDashboard";
import APIService from "../../Service/APIService";
import { Flex, Spin, Input } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import TitleBar from "../../common/title-bar/title-bar.js";
import CommonButton from "../../common/button/button.js";
import TabFade from "../../common/tab-fade";
import { useAuth } from "../AuthProvider.js";
import {
  buildApprovalQueues,
  fetchAllApprovalQueuePages,
} from "./approvalQueuePolicy.js";

const ApprovalsDashboard = () => {
  const [approvalData, setApprovalData] = React.useState({
    standard: [],
    prepaid: [],
  });
  const [activeTab, setActiveTab] = React.useState("standard");
  const [loading, setLoading] = React.useState(true);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const { user } = useAuth();

  const formatApprovalOrder = React.useCallback((order) => {
    const orderDate = order?.OrderDate
      ? dayjs(order.OrderDate).format("MM-DD-YYYY - HH:mm")
      : "";
    const orderTotalValue = Number(order?.OrderTotal ?? 0);

    return {
      ...order,
      OrderDate: orderDate,
      OrderTotal: `$${orderTotalValue.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`,
      OrderType:
        order?.OrderType === "DO"
          ? "Direct Order"
          : order?.OrderType === "CO"
          ? "Consignment Order"
          : order?.OrderType === "PU"
          ? "Product Usage"
          : order?.OrderType,
    };
  }, []);

  const getData = React.useCallback(async () => {
    const apiServe = new APIService();
    const [
      productUsageOrders,
      consignmentOrders,
      pendingDirectOrders,
      approvedDirectOrders,
    ] =
      await Promise.all([
        fetchAllApprovalQueuePages(
          apiServe,
          "getproductusageorders?type=Pending"
        ),
        fetchAllApprovalQueuePages(
          apiServe,
          "getconsignmentorders?type=Pending"
        ),
        fetchAllApprovalQueuePages(apiServe, "getdirectorders?type=Pending"),
        fetchAllApprovalQueuePages(apiServe, "getdirectorders?type=Approved"),
      ]);

    const queues = buildApprovalQueues({
      productUsageOrders,
      consignmentOrders,
      pendingDirectOrders,
      approvedDirectOrders,
    });
    const formattedQueues = {
      standard: queues.standard.map(formatApprovalOrder),
      prepaid: queues.prepaid.map(formatApprovalOrder),
    };

    setApprovalData(formattedQueues);

    if (
      formattedQueues.standard.length === 0 &&
      formattedQueues.prepaid.length > 0
    ) {
      setActiveTab("prepaid");
    }
  }, [formatApprovalOrder]);

  React.useEffect(() => {
    if (user.Type !== "Admin" && user.Type !== "Authorizer") {
      window.location.href = "/";
      return;
    }

    getData()
      .catch((error) => {
        console.log("Error fetching approvals data: ", error);
        setApprovalData({ standard: [], prepaid: [] });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getData, user.Type]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <TitleBar
        label="Approvals"
        filterBarComponents={
          <>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "standard"}
              onClick={() => setActiveTab("standard")}
            >
              Approvals
            </CommonButton>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "prepaid"}
              onClick={() => setActiveTab("prepaid")}
            >
              Prepaid Orders
            </CommonButton>
          </>
        }
        rightFilterBarComponents={
          <Input.Search
            placeholder="Search"
            allowClear
            style={{ height: "36px" }}
            onChange={(e) => {
              if (e.target.value === "") {
                setSearchKeyword("");
              }
            }}
            onSearch={(value) => setSearchKeyword(value || "")}
          />
        }
      />

      {loading && (
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      )}
      {!loading && (
        <TabFade activeKey={activeTab}>
          {activeTab === "standard" && (
            <GenericDashboard
              data={approvalData.standard}
              screen="ApprovalsDashboard"
              approvalTabType="standard"
              hideInternalSearch
              externalSearchKeyword={searchKeyword}
            />
          )}
          {activeTab === "prepaid" && (
            <GenericDashboard
              data={approvalData.prepaid}
              screen="ApprovalsDashboard"
              approvalTabType="prepaid"
              hideInternalSearch
              externalSearchKeyword={searchKeyword}
            />
          )}
        </TabFade>
      )}
    </div>
  );
};

export default ApprovalsDashboard;
